"use client";

import { useEffect, useRef } from "react";

/**
 * The v2 ("Dune") background system: a fixed, full-viewport dither field that
 * flows continuously, ripples row-by-row, accelerates with scroll delta and
 * displaces + brightens around the pointer.
 *
 * Ported from the design handoff (`components/raster/RasterField.jsx`). Mount
 * ONCE per page, behind all sections; sections sit at `z-index: 1`.
 *
 * Deviations from the handoff, both deliberate:
 *   - `prefers-reduced-motion` renders a single static frame (no rAF loop, no
 *     pointer displacement).
 *   - Coarse pointers (phones/tablets) get a coarser cell + lower frame rate;
 *     the per-pixel loop is CPU-bound and this is a marketing page.
 */
export function RasterField({
  scale = 2.2,
  fps = 38,
  flow = 1.5,
  scrollGain = 0.95,
  cursorRadius = 0.3,
  on = "#E8E3D9",
  off = "#111111",
}: {
  scale?: number;
  fps?: number;
  flow?: number;
  scrollGain?: number;
  cursorRadius?: number;
  on?: string;
  off?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const st = useRef({ flow: 0, lastY: 0, lastFrame: 0, mx: -999, my: -999 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const cellScale = coarse ? scale * 1.6 : scale;
    const frameGap = 1000 / (coarse ? 24 : fps);

    let raf = 0;
    let stopped = false;
    let W = 0;
    let H = 0;
    let GH = 0;
    let grain: Float32Array | null = null;
    let ridgeH: Float32Array | null = null;
    let ridgeS: Float32Array | null = null;
    let img: ImageData | null = null;

    /* value-noise → fbm, the source of the dune grain */
    const hash = (x: number, y: number) => {
      const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
      return s - Math.floor(s);
    };
    const sm = (t: number) => t * t * (3 - 2 * t);
    const noise = (x: number, y: number) => {
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      const xf = sm(x - xi);
      const yf = sm(y - yi);
      const a = hash(xi, yi);
      const b = hash(xi + 1, yi);
      const c = hash(xi, yi + 1);
      const d = hash(xi + 1, yi + 1);
      return (a * (1 - xf) + b * xf) * (1 - yf) + (c * (1 - xf) + d * xf) * yf;
    };
    const fbm = (x: number, y: number) => {
      let v = 0;
      let a = 0.5;
      let fr = 1;
      for (let i = 0; i < 4; i++) {
        v += a * noise(x * fr, y * fr);
        fr *= 2;
        a *= 0.5;
      }
      return v;
    };

    const build = () => {
      W = Math.max(1, Math.floor(canvas.clientWidth / cellScale));
      H = Math.max(1, Math.floor(canvas.clientHeight / cellScale));
      canvas.width = W;
      canvas.height = H;
      GH = H * 2;
      grain = new Float32Array(W * GH);
      for (let y = 0; y < GH; y++) {
        for (let x = 0; x < W; x++) grain[y * W + x] = fbm((x / W) * 6, (y / GH) * 9) - 0.5;
      }
      /* three soft dune ridges in the lower half */
      const ridges = [
        { base: 0.62, amp: 0.1, freq: 2.1, shade: 0.1 },
        { base: 0.74, amp: 0.08, freq: 3.4, shade: 0.14 },
        { base: 0.86, amp: 0.06, freq: 5.2, shade: 0.2 },
      ];
      ridgeH = new Float32Array(W * 3);
      ridgeS = new Float32Array(3);
      ridges.forEach((r, k) => {
        ridgeS![k] = r.shade;
        for (let x = 0; x < W; x++) {
          ridgeH![k * W + x] = (r.base + (fbm((x / W) * r.freq, 0.3) - 0.5) * r.amp * 2) * H;
        }
      });
      img = ctx.createImageData(W, H);
    };

    /* Bayer 8×8 ordered-dither matrix */
    const bayer = [
      0, 32, 8, 40, 2, 34, 10, 42, 48, 16, 56, 24, 50, 18, 58, 26,
      12, 44, 4, 36, 14, 46, 6, 38, 60, 28, 52, 20, 62, 30, 54, 22,
      3, 35, 11, 43, 1, 33, 9, 41, 51, 19, 59, 27, 49, 17, 57, 25,
      15, 47, 7, 39, 13, 45, 5, 37, 63, 31, 55, 23, 61, 29, 53, 21,
    ];
    const hex = (c: string): [number, number, number] => [
      parseInt(c.slice(1, 3), 16),
      parseInt(c.slice(3, 5), 16),
      parseInt(c.slice(5, 7), 16),
    ];
    const onRGB = hex(on);
    const offRGB = hex(off);

    const draw = (offY: number, offX: number) => {
      if (!grain || !ridgeH || !ridgeS || !img) return;
      const data = img.data;
      const oy = ((Math.round(offY) % GH) + GH) % GH;
      const ox = ((Math.round(offX) % W) + W) % W;
      const { mx, my } = st.current;
      const phase = offY * 0.02;
      const rad = Math.max(26, Math.min(W, H) * cursorRadius);
      const rad2 = rad * rad;
      const active = !reduced && mx > -900;
      for (let y = 0; y < H; y++) {
        const rowG = ((y + oy) % GH) * W;
        const rowO = y * W;
        const base = 0.9 - (y / H) * 0.16;
        const dy = y - my;
        const dy2 = dy * dy;
        const nearRow = active && dy2 < rad2;
        /* two-wave shear — makes the sand visibly stream sideways */
        const shear = Math.round(Math.sin(y * 0.07 + phase) * 7 + Math.sin(y * 0.021 - phase * 0.6) * 4);
        for (let x = 0; x < W; x++) {
          let gx = (x + ox + shear + W) % W;
          let lift = 0;
          if (nearRow) {
            const dx = x - mx;
            const d2 = dx * dx + dy2;
            if (d2 < rad2) {
              const ff = Math.pow(1 - Math.sqrt(d2) / rad, 2);
              lift = ff * 0.58;
              gx = (gx + Math.round(dx * ff * 0.95) + W) % W;
            }
          }
          let b = base + grain[rowG + gx] * 0.42 + lift;
          if (y > ridgeH[x]) b -= ridgeS[0];
          if (y > ridgeH[W + x]) b -= ridgeS[1];
          if (y > ridgeH[2 * W + x]) b -= ridgeS[2];
          const t = (bayer[((y & 7) << 3) + (x & 7)] + 0.5) / 64;
          const i = (rowO + x) * 4;
          const c = b > t ? onRGB : offRGB;
          data[i] = c[0];
          data[i + 1] = c[1];
          data[i + 2] = c[2];
          data[i + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
    };

    const onResize = () => {
      build();
      draw(st.current.flow, st.current.flow * 0.22);
    };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
        st.current.mx = -999;
        st.current.my = -999;
        return;
      }
      st.current.mx = ((e.clientX - r.left) / r.width) * W;
      st.current.my = ((e.clientY - r.top) / r.height) * H;
    };
    const onLeave = () => {
      st.current.mx = -999;
      st.current.my = -999;
    };

    build();
    draw(0, 0);
    window.addEventListener("resize", onResize);

    if (reduced) {
      return () => window.removeEventListener("resize", onResize);
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave, { passive: true });

    const loop = () => {
      if (stopped) return;
      raf = requestAnimationFrame(loop);
      const now = performance.now();
      if (now - st.current.lastFrame < frameGap) return;
      st.current.lastFrame = now;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const dy = y - st.current.lastY;
      st.current.lastY = y;
      st.current.flow += flow + dy * scrollGain;
      draw(st.current.flow, st.current.flow * 0.3);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, [scale, fps, flow, scrollGain, cursorRadius, on, off]);

  return (
    <div className="al-raster" aria-hidden="true">
      <canvas ref={ref} />
    </div>
  );
}
